import React, { useState } from "react";
import { CheckCircle2, Phone, Mail, MapPin, Send, AlertCircle } from "lucide-react";
import { useScrollReveal } from "@/hooks/useScrollReveal";

export default function QuoteForm() {
  const { ref, visible } = useScrollReveal();
  const [form, setForm] = useState({
    fullName: "",
    phone: "",
    email: "",
    address: "",
    projectType: "",
    description: ""
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const projectTypeOptions = [
    "Driveway",
    "Foundation",
    "Concrete Slab",
    "Concrete Flooring",
    "Backyard Patio",
    "Sidewalk / Walkway",
    "Demolition",
    "Concrete Repair",
    "Commercial Project",
    "Other"
  ];

  const whyNccPoints = [
    "Dependable concrete work for residential and commercial projects",
    "Clear communication from estimate to finishing",
    "Attention to preparation, placement and finishing",
    "Concrete solutions built for long-term use",
    "Local Northwest Arkansas concrete contractor"
  ];

  const validate = () => {
    const e = {};
    if (!form.fullName.trim()) e.fullName = "This field is required";
    if (!form.phone.trim()) e.phone = "This field is required";
    else if (form.phone.replace(/\D/g, "").length < 10) e.phone = "Please enter a valid phone number";
    if (!form.email.trim()) e.email = "This field is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Please enter a valid email";
    if (!form.address.trim()) e.address = "This field is required";
    if (!form.projectType) e.projectType = "This field is required";
    if (!form.description.trim()) e.description = "This field is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      const firstError = document.querySelector("[data-error='true']");
      if (firstError) firstError.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSubmitting(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      console.log("Form data:", form);
      setSuccess(true);

      // Google Analytics - Lead generated
      if (typeof window.gtag === "function") {
        window.gtag("event", "generate_lead", {
          event_category: "form_submission",
          event_label: form.projectType || "Unknown",
          value: 1,
        });
      }
    } catch (err) {
      setErrors({ submit: "An error occurred. Please contact us directly." });
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setForm({ fullName: "", phone: "", email: "", address: "", projectType: "", description: "" });
    setErrors({});
    setSuccess(false);
  };

  const inputClass = (field) =>
    `w-full px-4 py-3 bg-white border-2 rounded-sm text-[hsl(var(--navy))] placeholder-[hsl(var(--charcoal))]/40 focus:outline-none transition-colors ${
      errors[field] ? "border-[hsl(var(--destructive))]" : "border-[hsl(var(--concrete))] focus:border-[hsl(var(--accent))]"
    }`;

  return (
    <section id="quote" className="bg-[hsl(var(--navy))] py-20 md:py-28 concrete-texture relative overflow-hidden">
      <div className="absolute inset-0 rebar-trace opacity-20" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          ref={ref}
          className={`grid lg:grid-cols-5 gap-10 lg:gap-16 items-start transition-all duration-700 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          {/* Left: Why NCC */}
          <div className="lg:col-span-2">
            <div className="inline-flex items-center gap-2 mb-4">
              <div className="w-8 h-0.5 bg-[hsl(var(--card))]" />
              <span className="section-label text-[#194d32] text-base">
                REQUEST A FREE ESTIMATE
              </span>
            </div>
            <h2 className="headline text-white text-4xl sm:text-5xl mb-6">
              Request Your Free Estimate
            </h2>
            <div className="bg-white/5 border border-white/10 rounded-sm p-6 mb-6">
              <h3 className="font-heading text-xl tracking-wide mb-4 uppercase text-[#194d32]">
                Why Choose NCC?
              </h3>
              <ul className="space-y-3">
                {whyNccPoints.map((point, i) => (
                  <li key={i} className="flex items-start gap-3 text-white/80 text-sm leading-relaxed">
                    <CheckCircle2 size={18} className="flex-shrink-0 mt-0.5 text-[#194d32]" />
                    {point}
                  </li>
                ))}
              </ul>
            </div>
            {/* Quick contact - mailto as primary */}
            <div className="space-y-3">
              <a
                href="mailto:naturalconcrete.contruction@gmail.com"
                onClick={() => {
                  if (typeof window.gtag === "function") {
                    window.gtag("event", "contact_email", {
                      event_category: "engagement",
                      event_label: "Quote Form Email",
                    });
                  }
                }}
                className="flex items-center gap-3 text-white/80 hover:text-[hsl(var(--accent))] transition-colors"
              >
                <Mail size={18} className="text-[#194d32]" />
                naturalconcrete.contruction@gmail.com
              </a>
              <a
                href="tel:+14794227180"
                onClick={() => {
                  if (typeof window.gtag === "function") {
                    window.gtag("event", "contact_phone", {
                      event_category: "engagement",
                      event_label: "Quote Form Phone",
                    });
                  }
                }}
                className="flex items-center gap-3 text-white/80 hover:text-[hsl(var(--accent))] transition-colors"
              >
                <Phone size={18} className="text-[#194d32]" />
                479-422-7180
              </a>
              <div className="flex items-center gap-3 text-white/80">
                <MapPin size={18} className="text-[#194d32]" />
                Northwest Arkansas & Surrounding Areas
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div className="lg:col-span-3">
            {success ? (
              <div className="bg-white rounded-sm p-10 text-center animate-fade-in-up">
                <div className="flex items-center justify-center w-16 h-16 bg-[hsl(var(--accent))]/10 rounded-full mx-auto mb-5">
                  <CheckCircle2 size={40} className="text-[hsl(var(--accent))]" />
                </div>
                <h3 className="headline text-3xl text-[hsl(var(--navy))] mb-3">
                  Thank You!
                </h3>
                <p className="text-[hsl(var(--charcoal))]/70 leading-relaxed max-w-md mx-auto mb-6">
                  Thank you for contacting Naturals Concrete Construction. We
                  received your project information and will contact you shortly.
                </p>
                <button
                  onClick={resetForm}
                  className="bg-[hsl(var(--navy))] text-white font-heading text-base tracking-wider px-6 py-3 rounded-sm hover:bg-[hsl(var(--navy))]/90 transition-colors focus-ring"
                >
                  Send Another Request
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white rounded-sm p-6 sm:p-8 space-y-5" noValidate>
                <div className="grid sm:grid-cols-2 gap-5">
                  {/* Full Name */}
                  <div>
                    <label className="block text-sm font-bold text-[hsl(var(--navy))] mb-1.5 uppercase tracking-wider">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      value={form.fullName}
                      onChange={(e) => handleChange("fullName", e.target.value)}
                      className={inputClass("fullName")}
                      data-error={!!errors.fullName}
                    />
                    {errors.fullName && (
                      <p className="text-[hsl(var(--destructive))] text-xs mt-1 flex items-center gap-1">
                        <AlertCircle size={12} /> {errors.fullName}
                      </p>
                    )}
                  </div>
                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-bold text-[hsl(var(--navy))] mb-1.5 uppercase tracking-wider">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => handleChange("phone", e.target.value)}
                      className={inputClass("phone")}
                      data-error={!!errors.phone}
                    />
                    {errors.phone && (
                      <p className="text-[hsl(var(--destructive))] text-xs mt-1 flex items-center gap-1">
                        <AlertCircle size={12} /> {errors.phone}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  {/* Email */}
                  <div>
                    <label className="block text-sm font-bold text-[hsl(var(--navy))] mb-1.5 uppercase tracking-wider">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      className={inputClass("email")}
                      data-error={!!errors.email}
                    />
                    {errors.email && (
                      <p className="text-[hsl(var(--destructive))] text-xs mt-1 flex items-center gap-1">
                        <AlertCircle size={12} /> {errors.email}
                      </p>
                    )}
                  </div>
                  {/* Address */}
                  <div>
                    <label className="block text-sm font-bold text-[hsl(var(--navy))] mb-1.5 uppercase tracking-wider">
                      Project Address / City *
                    </label>
                    <input
                      type="text"
                      value={form.address}
                      onChange={(e) => handleChange("address", e.target.value)}
                      className={inputClass("address")}
                      data-error={!!errors.address}
                    />
                    {errors.address && (
                      <p className="text-[hsl(var(--destructive))] text-xs mt-1 flex items-center gap-1">
                        <AlertCircle size={12} /> {errors.address}
                      </p>
                    )}
                  </div>
                </div>

                {/* Project Type */}
                <div>
                  <label className="block text-sm font-bold text-[hsl(var(--navy))] mb-1.5 uppercase tracking-wider">
                    Project Type *
                  </label>
                  <select
                    value={form.projectType}
                    onChange={(e) => handleChange("projectType", e.target.value)}
                    className={inputClass("projectType")}
                    data-error={!!errors.projectType}
                  >
                    <option value="">Choose an option</option>
                    {projectTypeOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                  {errors.projectType && (
                    <p className="text-[hsl(var(--destructive))] text-xs mt-1 flex items-center gap-1">
                      <AlertCircle size={12} /> {errors.projectType}
                    </p>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-bold text-[hsl(var(--navy))] mb-1.5 uppercase tracking-wider">
                    Project Description *
                  </label>
                  <textarea
                    value={form.description}
                    onChange={(e) => handleChange("description", e.target.value)}
                    rows={4}
                    className={inputClass("description") + " resize-none"}
                    data-error={!!errors.description}
                  />
                  {errors.description && (
                    <p className="text-[hsl(var(--destructive))] text-xs mt-1 flex items-center gap-1">
                      <AlertCircle size={12} /> {errors.description}
                    </p>
                  )}
                </div>

                {/* Submit error */}
                {errors.submit && (
                  <div className="flex items-center gap-2 text-[hsl(var(--destructive))] text-sm bg-[hsl(var(--destructive))]/10 border border-[hsl(var(--destructive))]/20 rounded-sm px-4 py-3">
                    <AlertCircle size={16} />
                    {errors.submit}
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full flex items-center justify-center gap-2 hover:bg-[hsl(var(--accent))]/90 disabled:opacity-60 text-white font-heading text-lg tracking-wider px-6 py-4 rounded-sm transition-all focus-ring bg-[#194d32]"
                >
                  {submitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={20} />
                      Request My Estimate
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}